import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaBars, FaTimes, FaFileAlt, FaTruck, FaCog, FaSignOutAlt, FaChartBar, FaCaretDown, FaBox, FaPlug } from 'react-icons/fa';

const Dropdown = ({ icon: Icon, text, items, onSelect }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center px-4 py-2 hover:bg-blue-700 transition-colors duration-200 h-full"
      >
        <Icon size={20} className="mr-2" />
        <span className="text-sm font-medium whitespace-nowrap">{text}</span>
        <FaCaretDown size={12} className={`ml-1 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <div className="absolute top-full left-0 bg-white text-blue-900 shadow-md rounded-b-md overflow-hidden z-10 min-w-[200px]">
          {items.map((item) => (
            <button
              key={item.name}
              onClick={() => {
                onSelect(item.route);
                setIsOpen(false);
              }}
              className="block w-full text-left px-4 py-2 hover:bg-blue-100 transition-colors duration-200"
            >
              {item.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const Header = ({ enableAuth = true }) => {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [userRole, setUserRole] = useState('');

  useEffect(() => {
    if (enableAuth && !localStorage.getItem('isAuthenticated')) {
      navigate('/login');
    } else {
      setUserRole(localStorage.getItem('userRole'));
    }
  }, [navigate, enableAuth]);

  const handleLogout = () => {
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('userRole');
    navigate('/login');
  };

  const handleNavigation = (route) => {
    navigate(`/${route}`);
    setMenuOpen(false);
  };

  const navItems = [
    { icon: FaFileAlt, text: 'Order Entry', route: 'order-entry', roles: ['superadmin', 'admin', 'user'] },
    { icon: FaBox, text: 'Packing Entry', route: 'packing-entry', roles: ['superadmin', 'admin', 'packer', 'user'] },
    { icon: FaTruck, text: 'Dispatch Entry', route: 'dispatch-entry', roles: ['superadmin', 'admin'] },
  ];

  const wireItems = [
    { name: 'Wire', route: 'wire/wire', roles: ['superadmin', 'admin', 'user'] },
    { name: 'Wire Purchase', route: 'wire/wire-purchase', roles: ['superadmin', 'admin', 'user'] },
    { name: 'Wire Stock Adjustment', route: 'wire/wire-stock-adjustment', roles: ['superadmin'] },
  ].filter((item) => item.roles.includes(userRole));

  const reports = [
    { name: 'Register', route: 'report/register' },
    { name: 'Order Register', route: 'report/order-register' },
    { name: 'Packing Register', route: 'report/packing-register' },
    { name: 'Dispatch Register', route: 'report/dispatch-register' },
  ];

  const products = [
    { name: 'PDI', route: 'pdimenu' },
    { name: 'FAI', route: 'fdimenu' },
    { name: 'Daily Production Report', route: 'dailyproductionmenu' },
  ];

  return (
    <header className="bg-blue-600 text-white shadow-md sticky top-0 z-50">
      <div className="container mx-auto flex items-center justify-between h-14 px-4">
        <button className="lg:hidden focus:outline-none" onClick={() => setMenuOpen(!menuOpen)}>
          {menuOpen ? <FaTimes size={24} /> : <FaBars size={24} />}
        </button>

        <nav className={`lg:flex flex-col lg:flex-row lg:static absolute top-14 left-0 w-full bg-blue-600 lg:bg-transparent lg:w-auto transition-all duration-300 ${menuOpen ? 'block' : 'hidden'}`}>
          <ul className="lg:flex lg:items-center space-y-2 lg:space-y-0 lg:space-x-4 px-4 lg:px-0">
            {navItems
              .filter((item) => item.roles.includes(userRole))
              .map((item) => (
                <li key={item.route}>
                  <button className="flex items-center px-4 py-2 hover:bg-blue-700 transition-colors duration-200" onClick={() => handleNavigation(item.route)}>
                    <item.icon size={20} className="mr-2" /> {item.text}
                  </button>
                </li>
              ))}

            {['superadmin', 'admin', 'user'].includes(userRole) && wireItems.length > 0 ? (
              <li>
                <Dropdown icon={FaPlug} text="Wire" items={wireItems} onSelect={handleNavigation} />
              </li>
            ) : null}

            {userRole === 'superadmin' ? (
              <li>
                <button className="flex items-center px-4 py-2 hover:bg-blue-700 transition-colors duration-200" onClick={() => handleNavigation('admin')}>
                  <FaCog size={20} className="mr-2" /> Administration
                </button>
              </li>
            ) : null}

            {userRole === 'superadmin' ? (
              <li>
                <Dropdown icon={FaChartBar} text="Reports" items={reports} onSelect={handleNavigation} />
              </li>
            ) : null}

            {['superadmin', 'admin', 'qc'].includes(userRole) ? (
              <li>
                <Dropdown icon={FaChartBar} text="Products" items={products} onSelect={handleNavigation} />
              </li>
            ) : null}

            <li>
              <button className="flex items-center px-4 py-2 text-red-100 hover:text-white hover:bg-red-700 transition-colors duration-200" onClick={handleLogout}>
                <FaSignOutAlt size={20} className="mr-2" /> Exit
              </button>
            </li>
          </ul>
        </nav>
      </div>
    </header>
  );
};

export default Header;
